import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Archive, ArrowClockwise, ArrowCounterClockwise, Bell, CaretDown, CaretRight, CheckCircle, Copy, HourglassMedium, MagnifyingGlass, NotePencil, PaperPlaneTilt, PenNib, Plus, Prohibit, WarningCircle, X,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FlipCard, FlipKopf, ImmoButton, KpiReihe, KpiTile, LeerZustand, SchwebenderToast, SegmentedControl, useToastPill, type Segment,
} from '../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import EmailVerfassen from '../../emails/EmailVerfassen';
import { supabase } from '../../../../lib/supabase/client';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { signaturKennzahlen } from '../../../../lib/dokumente/kennzahlen';
import { anzeigeStatus, ANZEIGE_STATUS_LABEL, type AnzeigeStatus, type SignaturAktion } from '../../../../lib/dokumente/signatur';
import { filtereVorgaenge, objekteAus, rubrikenAus, passtRubrik } from '../../../../lib/dokumente/signaturFilter';
import { signaturDateiname, versandBetreff, versandEmpfaenger, versandText } from '../../../../lib/dokumente/signaturVersand';
import { naechsterUnterzeichner } from '../../../../lib/signatur/vorgangAktionen';
import {
  brecheVorgangAb, holeZwischenstand, kopiereAlsEntwurf, loescheVorgang, oeffneSigniertesPdf, sendeErneut, verschickeVorgang,
} from '../../../../lib/dokumente/signaturApi';
import { useSignaturVorgaenge } from '../../../../lib/dokumente/useSignaturVorgaenge';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import type { SignaturVorgang } from '../../../../lib/dokumente/signaturTypen';
import SignaturEditor from '../SignaturEditor';
import { VorgangKarte } from './VorgangKarte';
import { AkteAblegenDialog, VonHandDialog, ZwischenstandDialog } from './SignaturDialoge';
import { MAIL_FORM_META } from '../../../../components/island/form/mailMeta';

type StatusWahl = 'alle' | AnzeigeStatus;
type Rueckfrage = { art: 'verschicken' | 'erinnern' | 'abbrechen' | 'loeschen'; v: SignaturVorgang };

const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);
const DATUM: React.CSSProperties = { height: 44, padding: '0 12px', borderRadius: 14, border: 'none', fontSize: 13, fontWeight: 700, background: 'var(--bg)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', minWidth: 0 };
const SELECT: React.CSSProperties = { ...DATUM, appearance: 'none', padding: '0 34px 0 12px', cursor: 'pointer' };

function Dropdown({ label, wert, onChange, optionen, leer }: { label: string; wert: string; onChange: (v: string) => void; optionen: readonly string[]; leer: string }) {
  return (
    <span className="relative flex items-center" style={{ flex: '0 1 200px', minWidth: 140 }}>
      <select aria-label={label} value={wert} onChange={e => onChange(e.target.value)} style={{ ...SELECT, width: '100%' }}>
        <option value="">{leer}</option>
        {optionen.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <CaretDown weight="bold" size={11} color="var(--ink3)" style={{ position: 'absolute', right: 12, pointerEvents: 'none' }} />
    </span>
  );
}

/** Signieren (Handoff Dokumente §6.4): KPIs, Filter, Vorgangskarten mit Aktionen je Status. Editor bis E5 unverändert. */
export function SignierenTab({ userProfile, onVorlagen }: { userProfile?: { id?: string } & Record<string, unknown>; /** #515: zum Bereich Vorlagen */ onVorlagen?: () => void }) {
  const [params, setParams] = useSearchParams();
  const bearbeiten = params.get('bearbeiten');
  const { liste, fehler, bilder, neuLaden } = useSignaturVorgaenge();
  const { openForm, closeForm } = useIslandActions();
  const { mobil } = useMobilModus();
  const [toast, zeigeToast] = useToastPill(3200);
  const userId = typeof userProfile?.id === 'string' ? userProfile.id : null;

  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  useEffect(() => { ladeObjekte().then(setObjekte).catch(() => setObjekte([])); }, []);

  // ── Filter ────────────────────────────────────────────────────────────────
  const [suche, setSuche] = useState('');
  const [status, setStatus] = useState<StatusWahl>('alle');
  const [rubrik, setRubrik] = useState('');
  const [objekt, setObjekt] = useState('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const vorgaenge = useMemo(() => (liste ?? []).filter(v => !v.ist_vorlage), [liste]);
  const anzahlVorlagen = useMemo(() => (liste ?? []).filter(v => v.ist_vorlage).length, [liste]);
  const kpi = useMemo(() => signaturKennzahlen(vorgaenge), [vorgaenge]);
  const basis = useMemo(() => filtereVorgaenge(vorgaenge, { suche, objekt: objekt || null, von, bis }).filter(v => passtRubrik(v, rubrik || null)), [vorgaenge, suche, objekt, von, bis, rubrik]);
  const sichtbar = status === 'alle' ? basis : basis.filter(v => anzeigeStatus(v) === status);
  // Rubrik-Auswahl nur, wenn überhaupt ein Vorgang eine Rubrik trägt
  const rubriken = useMemo(() => (vorgaenge.some(v => (v.vorlage_kategorie ?? '').trim()) ? rubrikenAus(vorgaenge) : []), [vorgaenge]);
  const objektListe = useMemo(() => objekteAus(vorgaenge), [vorgaenge]);
  const gefiltert = !!(suche.trim() || status !== 'alle' || rubrik || objekt || von || bis);
  const zuruecksetzen = () => { setSuche(''); setStatus('alle'); setRubrik(''); setObjekt(''); setVon(''); setBis(''); };
  const zaehle = (s: AnzeigeStatus) => basis.filter(v => anzeigeStatus(v) === s).length;
  const segmente: Segment<StatusWahl>[] = [
    { key: 'alle', label: 'Alle', anzahl: basis.length },
    ...(['entwurf', 'verschickt', 'abgeschlossen', 'abgebrochen'] as const).map(s => ({ key: s, label: ANZEIGE_STATUS_LABEL[s], anzahl: zaehle(s) })),
    ...(vorgaenge.some(v => anzeigeStatus(v) === 'ausgefuellt') ? [{ key: 'ausgefuellt' as const, label: 'Ausgefüllt', anzahl: zaehle('ausgefuellt') }] : []),
  ];

  // ── Aktionen ──────────────────────────────────────────────────────────────
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [frage, setFrage] = useState<Rueckfrage | null>(null);
  const [ablegen, setAblegen] = useState<SignaturVorgang | null>(null);
  const [vonHand, setVonHand] = useState<SignaturVorgang | null>(null);
  const [zwischenstand, setZwischenstand] = useState<{ v: SignaturVorgang; datei: { blob: Blob; name: string } } | null>(null);
  const oeffneEditor = (id: string) => setParams(p => { const n = new URLSearchParams(p); n.set('bearbeiten', id); return n; });
  const [editorGeaendert, setEditorGeaendert] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);
  const schliesseEditor = () => { setEditorGeaendert(false); setVerwerfen(false); setParams(p => { const n = new URLSearchParams(p); n.delete('bearbeiten'); return n; }); neuLaden(); };
  const abbrechen = () => (editorGeaendert ? setVerwerfen(true) : schliesseEditor());

  const mitLauf = async (v: SignaturVorgang, f: () => Promise<void>) => {
    setLaeuft(v.id);
    try { await f(); } catch (e) { zeigeToast(fehlerText(e, 'Aktion fehlgeschlagen'), true); } finally { setLaeuft(null); }
  };

  const perMail = async (v: SignaturVorgang) => {
    if (!v.signiert_path) return;
    const { data: konten } = await supabase.from('email_konten').select('id, email_adresse, bezeichnung, absender_name').eq('aktiv', true).order('ist_standard', { ascending: false }).limit(1);
    const konto = konten?.[0];
    if (!konto) { zeigeToast('Kein aktives E-Mail-Postfach gefunden.', true); return; }
    openForm(
      <EmailVerfassen variant="pille"
        konto={{ id: konto.id as string, email_adresse: konto.email_adresse as string, bezeichnung: (konto.bezeichnung as string | null) || (konto.absender_name as string | null) || (konto.email_adresse as string) }}
        autoAnhaengeStorage={[{ bucket: 'signaturen', path: v.signiert_path, name: signaturDateiname(v) }]}
        draft={{ an: versandEmpfaenger(v), betreff: versandBetreff(v), text_inhalt: '' }}
        autoText={versandText(v)} onClose={() => closeForm()} onSent={() => closeForm()} />,
      'Dokument senden', 640, MAIL_FORM_META,
    );
  };

  const onAktion = (a: SignaturAktion, v: SignaturVorgang) => {
    switch (a) {
      case 'bearbeiten': oeffneEditor(v.id); break;
      case 'verschicken': case 'erinnern': case 'abbrechen': case 'loeschen': setFrage({ art: a, v }); break;
      case 'zwischenstand': void mitLauf(v, async () => setZwischenstand({ v, datei: await holeZwischenstand(v) })); break;
      case 'vonHand': setVonHand(v); break;
      case 'herunterladen': void mitLauf(v, () => oeffneSigniertesPdf(v)); break;
      case 'email': void perMail(v); break;
      case 'ablegen': setAblegen(v); break;
      case 'kopieren': void mitLauf(v, async () => { const neu = await kopiereAlsEntwurf(v, { mitEmail: true, userId }); neuLaden(); oeffneEditor(neu.id); }); break;
    }
  };

  const bestaetige = async () => {
    if (!frage) return;
    const { art, v } = frage;
    setFrage(null);
    await mitLauf(v, async () => {
      if (art === 'verschicken') { await verschickeVorgang(v.id); zeigeToast(`„${v.titel}“ verschickt`); }
      if (art === 'erinnern') { const d = await sendeErneut(v.id); zeigeToast(`Einladung erneut an ${d.name || d.email || 'Unterzeichner'} verschickt`); }
      if (art === 'abbrechen') { await brecheVorgangAb(v.id); zeigeToast(`„${v.titel}“ abgebrochen – Signier-Links sind ungültig`); }
      if (art === 'loeschen') { await loescheVorgang(v); zeigeToast(`„${v.titel}“ gelöscht`); }
      neuLaden();
    });
  };

  const frageText = (r: Rueckfrage): { titel: string; text: string; knopf: string; gefahr: boolean } => {
    const n = naechsterUnterzeichner(r.v.unterzeichner)?.unterzeichner;
    const erster = r.v.unterzeichner[0];
    switch (r.art) {
      case 'verschicken': return { titel: 'Vorgang verschicken?', text: `Die Einladung geht an ${erster?.name || 'den ersten Unterzeichner'} <${erster?.email || '–'}>.`, knopf: 'Verschicken', gefahr: false };
      case 'erinnern': return { titel: 'Erinnerung senden?', text: `Einladung erneut an ${n?.name || 'Unterzeichner'} <${n?.email || '–'}> schicken. Der bisherige Signier-Link bleibt gültig.`, knopf: 'Erinnerung senden', gefahr: false };
      case 'abbrechen': return { titel: 'Vorgang abbrechen?', text: 'Die verschickten Signier-Links werden ungültig. Der Vorgang bleibt als „Abgebrochen“ erhalten.', knopf: 'Abbrechen', gefahr: true };
      case 'loeschen': return {
        titel: 'Vorgang löschen?', gefahr: true, knopf: 'Löschen',
        text: r.v.status === 'verschickt' ? 'Die bereits verschickten Signier-Links werden dadurch ungültig.'
          : r.v.status === 'abgeschlossen' ? 'Auch das fertig signierte Dokument samt Protokoll wird unwiderruflich entfernt.' : 'Der Vorgang wird unwiderruflich gelöscht.',
      };
    }
  };

  // Noch nicht in der Liste (z. B. frisch aus einer Vorlage angelegt) → einzeln nachladen
  const [einzeln, setEinzeln] = useState<{ id: string; v: SignaturVorgang | null } | null>(null);
  const inListe = bearbeiten && bearbeiten !== 'neu' ? liste?.find(v => v.id === bearbeiten) : undefined;
  useEffect(() => {
    if (!bearbeiten || bearbeiten === 'neu' || inListe || !liste || einzeln?.id === bearbeiten) return;
    let aktiv = true;
    supabase.from('signatur_vorgaenge').select('*').eq('id', bearbeiten).maybeSingle()
      .then(({ data }) => { if (aktiv) setEinzeln({ id: bearbeiten, v: (data as SignaturVorgang | null) ?? null }); });
    return () => { aktiv = false; };
  }, [bearbeiten, inListe, liste, einzeln?.id]);
  const editorVorgang = inListe ?? (einzeln && einzeln.id === bearbeiten ? einzeln.v ?? undefined : undefined);
  const editorSuchtNoch = !!bearbeiten && bearbeiten !== 'neu' && !inListe && (!liste || einzeln?.id !== bearbeiten);
  const editorTitel = bearbeiten === 'neu' ? 'Neuer Signatur-Vorgang'
    : editorVorgang?.ist_vorlage ? 'Vorlage bearbeiten' : editorVorgang?.status === 'entwurf' ? 'Vorgang bearbeiten' : 'Vorgang ansehen';
  const editor = !bearbeiten ? null
    : bearbeiten === 'neu' ? <SignaturEditor key="neu" vorgang={null} userProfile={userProfile} onZurueck={schliesseEditor} onGeaendert={setEditorGeaendert} onNeuerVorgang={oeffneEditor} />
    : editorVorgang ? <SignaturEditor key={bearbeiten} vorgang={editorVorgang} userProfile={userProfile} onZurueck={schliesseEditor} onGeaendert={setEditorGeaendert} onNeuerVorgang={oeffneEditor} />
    : !editorSuchtNoch ? <LeerZustand icon={WarningCircle} titel="Vorgang nicht gefunden"><ImmoButton onClick={schliesseEditor} style={{ marginTop: 6 }}>Zurück zur Liste</ImmoButton></LeerZustand>
    : <div className="animate-pulse" style={{ height: 320, borderRadius: 26, background: 'var(--fill2)' }} />;

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <FlipKopf titel={bearbeiten ? editorTitel : 'Signieren'} offen={!!bearbeiten} label="Neuer Vorgang"
        abbrechenLabel={bearbeiten === 'neu' || editorGeaendert ? 'Abbrechen' : 'Schließen'} onToggle={() => (bearbeiten ? abbrechen() : oeffneEditor('neu'))} />
      <FlipCard gedreht={!!bearbeiten} hinten={editor} vorne={(
      <div className="flex flex-col min-w-0" style={{ gap: 14 }}>

      <KpiReihe minBreite={mobil ? 150 : 190}>
        <KpiTile layout="kachel" icon={NotePencil} ton="neutral" wert={kpi.entwuerfe} label="Entwürfe" onClick={() => setStatus('entwurf')} />
        <KpiTile layout="kachel" icon={HourglassMedium} ton="info" wert={kpi.wartend} label="Warten auf Unterschrift" onClick={() => setStatus('verschickt')} />
        <KpiTile layout="kachel" icon={CheckCircle} ton="ok" wert={kpi.abgeschlossen} label="Abgeschlossen" onClick={() => setStatus('abgeschlossen')} />
        <KpiTile layout="kachel" icon={Archive} ton="warn" wert={kpi.nichtAbgelegt} label="Noch nicht abgelegt" onClick={() => setStatus('abgeschlossen')} />
      </KpiReihe>

      {onVorlagen && anzahlVorlagen > 0 && (
        // #515: Vorlagen liegen im Bereich „Vorlagen“ – hier der Weg dorthin, damit sie nicht verloren wirken
        <button type="button" onClick={onVorlagen} className="immo-glas immo-hover-widget flex items-center text-left cursor-pointer"
          style={{ borderRadius: 18, padding: '10px 14px', gap: 10, border: 'none', color: 'var(--ink)' }}>
          <Copy size={18} weight="fill" color="var(--acc)" />
          <span className="flex-1 min-w-0" style={{ fontSize: 13 }}>
            <b>{anzahlVorlagen} Signatur-Vorlagen</b> <span style={{ color: 'var(--ink3)' }}>· Vorgang aus einer Vorlage starten oder Vorlagen bearbeiten</span>
          </span>
          <CaretRight size={14} weight="bold" color="var(--ink3)" />
        </button>
      )}

      <label className="immo-glas flex items-center" style={{ height: 44, borderRadius: 22, gap: 8, padding: '0 14px' }}>
        <MagnifyingGlass size={16} color="var(--ink3)" aria-hidden />
        <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Titel oder Bezug suchen…" aria-label="Titel oder Bezug suchen"
          className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
      </label>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <div className="immo-scroll" style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <SegmentedControl<StatusWahl> ariaLabel="Status" art="akzent" wert={status} onChange={setStatus} segmente={segmente} hoehe={mobil ? 44 : 34} className="flex-nowrap" />
        </div>
      </div>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        {rubriken.length > 0 && <Dropdown label="Rubrik" wert={rubrik} onChange={setRubrik} optionen={rubriken} leer="Alle Rubriken" />}
        {objektListe.length > 1 && <Dropdown label="Objekt" wert={objekt} onChange={setObjekt} optionen={objektListe} leer="Alle Objekte" />}
        <input type="date" value={von} onChange={e => setVon(e.target.value)} aria-label="Erstellt von" style={{ ...DATUM, flex: '0 1 160px' }} />
        <input type="date" value={bis} onChange={e => setBis(e.target.value)} aria-label="Erstellt bis" style={{ ...DATUM, flex: '0 1 160px' }} />
        {gefiltert && (
          <button type="button" onClick={zuruecksetzen} className="immo-hover-flaeche flex items-center cursor-pointer"
            style={{ height: 44, padding: '0 12px', gap: 6, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--acc)', fontSize: 13, fontWeight: 700 }}>
            <X weight="bold" size={12} />Filter zurücksetzen
          </button>
        )}
      </div>

      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      ) : !liste ? (
        <div className="flex flex-col" style={{ gap: 12 }} aria-busy="true">{Array.from({ length: 3 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 170, borderRadius: 26, background: 'var(--fill2)' }} />)}</div>
      ) : vorgaenge.length === 0 ? (
        <LeerZustand icon={PenNib} titel="Noch keine Dokumente zur Signatur" text="Lade ein PDF hoch, platziere Unterschrift- und Textfelder und verschicke es an die Unterzeichner.">
          <ImmoButton art="akzent" icon={Plus} onClick={() => oeffneEditor('neu')} style={{ marginTop: 6 }}>Neuer Vorgang</ImmoButton>
        </LeerZustand>
      ) : sichtbar.length === 0 ? (
        <LeerZustand icon={MagnifyingGlass} titel="Keine Treffer" text="Kein Vorgang passt zu den Filtern."><ImmoButton icon={X} onClick={zuruecksetzen} style={{ marginTop: 6 }}>Filter zurücksetzen</ImmoButton></LeerZustand>
      ) : (
        <div className="flex flex-col" style={{ gap: 12 }}>
          {sichtbar.map(v => <VorgangKarte key={v.id} v={v} bild={bilder[v.id]} laeuft={laeuft === v.id} onAktion={onAktion} />)}
          <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)', textAlign: 'center' }}>{sichtbar.length} von {vorgaenge.length} Vorgängen{gefiltert ? ' (gefiltert)' : ''}</span>
        </div>
      )}
      </div>
      )} />
      {verwerfen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Nicht gespeicherte Änderungen am Vorgang gehen verloren." bestaetigenLabel="Verwerfen"
          icon={ArrowCounterClockwise} onBestaetigen={schliesseEditor} onAbbrechen={() => setVerwerfen(false)} />
      )}

      {frage && (() => {
        const t = frageText(frage);
        return <BestaetigenDialog titel={t.titel} text={t.text} detail={<strong>{frage.v.titel}</strong>} bestaetigenLabel={t.knopf} gefahr={t.gefahr}
          icon={frage.art === 'loeschen' ? undefined : frage.art === 'abbrechen' ? Prohibit : frage.art === 'erinnern' ? Bell : PaperPlaneTilt}
          onBestaetigen={() => void bestaetige()} onAbbrechen={() => setFrage(null)} />;
      })()}
      {ablegen && <AkteAblegenDialog vorgang={ablegen} objekte={objekte} onSchliessen={() => setAblegen(null)} onFertig={m => { setAblegen(null); zeigeToast(m); neuLaden(); }} />}
      {zwischenstand && <ZwischenstandDialog datei={zwischenstand.datei} titel={zwischenstand.v.titel} onSchliessen={() => setZwischenstand(null)} />}
      {vonHand && <VonHandDialog vorgang={vonHand} onSchliessen={() => setVonHand(null)} onFertig={m => { setVonHand(null); zeigeToast(m); neuLaden(); }} />}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
